// BookTickets/lib/cart.ts
import type {
  Cart,
  Session,
  TicketQuantities,
  VoucherCart,
  VoucherQuantities,
} from '@booking/types/booking'

/**
 * True if cart exists and contains lines.
 */
export function cartHasLines(cart: Cart | null | undefined) {
  return Boolean(cart?.lines && cart.lines.length > 0)
}

/**
 * True if cart exists and contains voucher lines.
 */
export function cartHasVoucherLines(cart: Cart | VoucherCart | null | undefined) {
  return Boolean(cart?.voucherLines && cart.voucherLines.length > 0)
}

/**
 * Total from cart (already calculated server-side).
 */
export function calcTotalForCart(cart: Cart | VoucherCart | null): number {
  if (!cart) return 0
  //if (!cartHasLines(cart) && !cartHasVoucherLines(cart)) return 0
  return cart.total ?? 0
}

/**
 * Sum VAT values from cart lines.
 * (Only valid if API provides vatValue on lines.)
 */
export function calcCartVatTotal(cart: Cart | null | undefined): number {
  if (!cart || !cartHasLines(cart)) return 0
  return cart.lines.reduce((sum, line) => sum + (line.vatValue ?? 0), 0)
}

/**
 * Count total tickets across all variants.
 */
export function ticketCount(quantities: TicketQuantities): number {
  if (!quantities || quantities.length === 0) return 0
  return quantities.reduce((sum, n) => sum + (n.quantity ?? 0), 0) ?? 0
}

/**
 * Count total vouchers across all variants.
 */
export function voucherCount(quantities: VoucherQuantities): number {
  if (!quantities || quantities.length === 0) return 0
  return quantities.reduce((sum, n) => sum + (n.quantity ?? 0), 0) ?? 0
}

/**
 * Calculates the price of one ticket UUID in a session.
 * `session.prices` assumed to be keyed by ticket UUID.
 */
export function getUnitPriceForTicket(session: Session, categoryKey: string): number {
  const category = session.pricesByCategory.find((item) => item.category === categoryKey)
  return category?.price ?? 0
}

type CalcSessionTotalArgs = {
  session: Session
  quantities: TicketQuantities
}

/**
 * Canonical session total calculator (preferred API).
 * Uses booking "numbers" (variant quantities) + tickets map to find UUIDs, then reads session.prices.
 */
export function calcSessionTotal({ session, quantities }: CalcSessionTotalArgs): number {
  let total = 0

  for (const item of quantities) {
    if (!item.quantity || item.quantity <= 0) continue
    total += getUnitPriceForTicket(session, item.category) * item.quantity
  }

  return total
}

/**
 * Calculates subtotal for a cart using session prices, ignoring cart discounts/vouchers.
 * Useful for "subtotal before voucher" display.
 */
export function calcCartSubtotalForSession(args: { cart: Cart; session: Session }): number {
  const { cart, session } = args
  if (!cartHasLines(cart)) return 0

  let total = 0

  for (const line of cart.lines) {
    const qty = line.quantity ?? 0
    if (!qty) continue

    total += getUnitPriceForTicket(session, line.category) * qty
  }

  return total
}
